/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import { today } from '@internationalized/date';

/**
 * Utility functions for processing pass text content
 */

/**
 * Replaces year placeholders in 4th grade pass text with actual year values
 * Only processes text if the pass type is 'fourth-grade'
 * Uses UTC timezone to match system-wide pass date handling
 *
 * @param text - The text content that may contain year placeholders
 * @param passType - The type of pass (e.g., 'fourth-grade', 'annual', etc.)
 * @returns Processed text with year replacements if applicable, original text otherwise
 */
export const processPassText = (text: string, passType?: string): string => {
    // Only process 4th grade passes
    if (passType !== 'fourth-grade') {
        return text;
    }

    const currentYear = today('UTC').year;
    const nextYear = currentYear + 1;

    return text
        .replace('of this year', currentYear.toString())
        .replace('of next year', nextYear.toString());
};

/**
 * Formats pass title by prepending the appropriate year for annual-type passes
 * Uses UTC timezone to match system-wide pass date handling
 *
 * @param title - The original pass title
 * @param passType - The type of pass (e.g., 'annual', 'resident', 'nonresident', etc.)
 * @param overrideATBPassYear - The year to override the calculated year, if not provided, year is calculated based on pass type
 * @returns Title with year prefix for annual-type passes, original title otherwise
 */
export const formatPassTitle = (
    title: string,
    passType: string,
    overrideATBPassYear?: number
): string => {
    const currentYear = today('UTC').year;

    // Resident and Non-Resident passes launch Jan 1, 2026
    // During 2025 pre-launch period, show 2026; after Jan 1, 2026, show current year
    if (passType === 'resident' || passType === 'nonresident') {
        const year = overrideATBPassYear || (currentYear === 2025 ? 2026 : currentYear);
        return `${year} ${title}`;
    }

    // Annual passes use current year
    if (passType.includes('annual')) {
        const year = overrideATBPassYear || currentYear;
        return `${year} ${title}`;
    }

    return title;
};
